'use client';

import { createClient } from '@/lib/supabase/client';
import type { PantryStatus, AvailabilityLevel } from '@/lib/pantryData';

export interface PantryStatusRow {
  id: string;
  pantry_status: PantryStatus;
  huling_update: string;
  updated_at: string;
}

/**
 * Fetch all pantry status overrides from Supabase.
 */
export async function fetchAllPantryStatuses(): Promise<PantryStatusRow[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('pantry_statuses')
    .select('id, pantry_status, huling_update, updated_at');

  if (error) {
    console.error('fetchAllPantryStatuses error:', error.message);
    return [];
  }
  return (data as PantryStatusRow[]) ?? [];
}

/**
 * Upsert a single pantry's status (and optionally item availability) in Supabase.
 * Also updates the pantries table so realtime subscribers on that table receive the change.
 */
export async function upsertPantryStatus(
  pantryId: string,
  status: PantryStatus,
  hulingUpdate: string,
  itemAvailability?: Record<string, AvailabilityLevel>,
): Promise<void> {
  const supabase = createClient();

  // 1. Keep pantry_statuses in sync (legacy / audit trail)
  const { error: statusError } = await supabase.from('pantry_statuses').upsert(
    {
      id: pantryId,
      pantry_status: status,
      huling_update: hulingUpdate,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'id' },
  );

  if (statusError) {
    console.error('upsertPantryStatus (pantry_statuses) error:', statusError.message);
  }

  // 2. Update the pantries table directly — this triggers the realtime UPDATE
  //    subscription in MapClientWrapper so the map reflects the new status instantly.
  const patch: Record<string, unknown> = {
    pantry_status: status,
    huling_update: hulingUpdate,
  };
  if (itemAvailability !== undefined) {
    patch.item_availability = itemAvailability;
  }

  const { error: pantryError } = await supabase
    .from('pantries')
    .update(patch)
    .eq('id', pantryId);

  if (pantryError) {
    console.error('upsertPantryStatus (pantries) error:', pantryError.message);
  }
}

/**
 * Subscribe to real-time changes on pantry_statuses table.
 * Returns an unsubscribe function.
 */
export function subscribeToPantryStatusChanges(
  onUpdate: (row: PantryStatusRow) => void,
  channelName = 'pantry_statuses_realtime',
): () => void {
  const supabase = createClient();

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'pantry_statuses' },
      (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          onUpdate(payload.new as PantryStatusRow);
        }
      },
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
